import React, { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, X, Plus, Minus, Trash2, CheckCircle, ArrowRight, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { productsData, Product } from '../data/productsData';
interface CartItem extends Product {
  quantity: number;
}
export function StorePage() {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<'browsing' | 'checkout' | 'confirmation'>('browsing');
  const [filter, setFilter] = useState<string>('All');
  const categories = ['All', 'Books', 'Apparel', 'Media', 'Gifts'];
  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => item.id === product.id ? {
          ...item,
          quantity: item.quantity + 1
        } : item);
      }
      return [...prev, {
        ...product,
        quantity: 1
      }];
    });
    setIsCartOpen(true);
  };
  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.quantity + delta);
        return {
          ...item,
          quantity: newQty
        };
      }
      return item;
    }));
  };
  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const filteredProducts = filter === 'All' ? productsData : productsData.filter(p => p.category === filter);
  const handleCheckout = () => {
    setCheckoutStep('checkout');
    setIsCartOpen(false);
    // Simulate a brief loading state for payment processing
    setTimeout(() => {
      setCheckoutStep('confirmation');
      setCart([]);
    }, 2000);
  };
  return <Layout data-id="element-1334">
      {/* Hero */}
      <div className="bg-[#0A1F44] pt-44 pb-20 text-center text-white relative overflow-hidden" data-id="element-1335">
        <div className="absolute inset-0 opacity-20" data-id="element-1336">
          <img src="https://cdn.magicpatterns.com/uploads/eV3wDE6FECgn2v5r4xzR2s/Gemini_Generated_Image_z2lqwuz2lqwuz2lq.png" alt="Store Background" className="w-full h-full object-cover" data-id="element-1337" />
        </div>
        <div className="container-custom relative z-10" data-id="element-1338">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-serif font-bold mb-6" data-id="element-1339">
            Online Store
          </motion.h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto" data-id="element-1340">
            Resources to equip you and apparel to represent your family.
          </p>
        </div>
      </div>

      {checkoutStep === 'browsing' && <section className="py-20 bg-white" data-id="element-1341">
          <div className="container-custom" data-id="element-1342">
            {/* Filters & Cart Trigger */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-6" data-id="element-1343">
              <div className="flex flex-wrap gap-2" data-id="element-1344">
                {categories.map(cat => <button key={cat} onClick={() => setFilter(cat)} className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${filter === cat ? 'bg-[#fc0719] text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1345">
                    {cat}
                  </button>)}
              </div>
              <button onClick={() => setIsCartOpen(true)} className="flex items-center gap-3 bg-[#0A1F44] text-white px-6 py-3 rounded-xl hover:bg-[#163A70] transition-colors relative" data-id="element-1346">
                <ShoppingCart className="w-5 h-5" data-id="element-1347" />
                <span className="font-bold" data-id="element-1348">View Cart</span>
                {cart.length > 0 && <span className="absolute -top-2 -right-2 bg-[#fc0719] text-white text-xs w-6 h-6 rounded-full flex items-center justify-center border-2 border-white" data-id="element-1349">
                    {cart.reduce((a, b) => a + b.quantity, 0)}
                  </span>}
              </button>
            </div>

            {/* Product Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1350">
              {filteredProducts.map(product => <motion.div key={product.id} layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden group hover:shadow-xl transition-shadow" data-id="element-1351">
                  <div className="aspect-square overflow-hidden relative" data-id="element-1352">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-1353" />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#0A1F44]" data-id="element-1354">
                      {product.category}
                    </div>
                  </div>
                  <div className="p-6" data-id="element-1355">
                    <h3 className="text-xl font-bold text-[#0A1F44] mb-2" data-id="element-1356">
                      {product.name}
                    </h3>
                    <p className="text-gray-500 text-sm mb-4 line-clamp-2" data-id="element-1357">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between" data-id="element-1358">
                      <span className="text-2xl font-bold text-[#fc0719]" data-id="element-1359">
                        ${product.price.toFixed(2)}
                      </span>
                      <Button onClick={() => addToCart(product)} variant="primary" size="sm" className="rounded-lg" data-id="element-1360">
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>}

      {checkoutStep === 'checkout' && <section className="py-20 bg-gray-50 min-h-[600px] flex items-center" data-id="element-1361">
          <div className="container-custom text-center" data-id="element-1362">
            <div className="max-w-md mx-auto bg-white p-12 rounded-3xl shadow-xl" data-id="element-1363">
              <div className="w-20 h-20 bg-[#fc0719]/10 rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-1364">
                <ShoppingBag className="w-10 h-10 text-[#fc0719] animate-pulse" data-id="element-1365" />
              </div>
              <h2 className="text-3xl font-serif font-bold text-[#0A1F44] mb-4" data-id="element-1366">
                Processing Payment
              </h2>
              <p className="text-gray-500 mb-8" data-id="element-1367">
                Please wait while we securely process your transaction...
              </p>
              <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden" data-id="element-1368">
                <motion.div initial={{
              width: 0
            }} animate={{
              width: '100%'
            }} transition={{
              duration: 2
            }} className="h-full bg-[#fc0719]" data-id="element-1369" />
              </div>
            </div>
          </div>
        </section>}

      {checkoutStep === 'confirmation' && <section className="py-20 bg-white min-h-[600px] flex items-center" data-id="element-1370">
          <div className="container-custom text-center" data-id="element-1371">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="max-w-2xl mx-auto" data-id="element-1372">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-1373">
                <CheckCircle className="w-12 h-12 text-green-600" data-id="element-1374" />
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#0A1F44] mb-6" data-id="element-1375">
                Thank You for Your Purchase!
              </h2>
              <p className="text-xl text-gray-600 mb-10" data-id="element-1376">
                Your order has been received and is being processed. A
                confirmation email has been sent to you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1377">
                <Button onClick={() => setCheckoutStep('browsing')} variant="primary" size="lg" className="px-10" data-id="element-1378">
                  Continue Shopping
                </Button>
                <Link to="/" data-id="element-1379">
                  <Button variant="outline" size="lg" className="px-10" data-id="element-1380">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>}

      {/* Cart Sidebar */}
      <AnimatePresence data-id="element-1381">
        {isCartOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsCartOpen(false)} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]" data-id="element-1382" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-[70] shadow-2xl flex flex-col" data-id="element-1383">
              <div className="p-6 border-b flex items-center justify-between" data-id="element-1384">
                <h2 className="text-2xl font-bold text-[#0A1F44] flex items-center gap-3" data-id="element-1385">
                  <ShoppingBag className="w-6 h-6" data-id="element-1386" /> Your Cart
                </h2>
                <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1387">
                  <X className="w-6 h-6 text-gray-400" data-id="element-1388" />
                </button>
              </div>

              <div className="flex-grow overflow-y-auto p-6 space-y-6" data-id="element-1389">
                {cart.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center" data-id="element-1390">
                    <ShoppingBag className="w-16 h-16 text-gray-200 mb-4" data-id="element-1391" />
                    <p className="text-gray-400 font-medium" data-id="element-1392">
                      Your cart is empty
                    </p>
                    <Button onClick={() => setIsCartOpen(false)} variant="outline" className="mt-6" data-id="element-1393">
                      Start Shopping
                    </Button>
                  </div> : cart.map(item => <div key={item.id} className="flex gap-4" data-id="element-1394">
                      <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0" data-id="element-1395">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-1396" />
                      </div>
                      <div className="flex-grow" data-id="element-1397">
                        <h4 className="font-bold text-[#0A1F44]" data-id="element-1398">
                          {item.name}
                        </h4>
                        <p className="text-[#fc0719] font-bold text-sm" data-id="element-1399">
                          ${item.price.toFixed(2)}
                        </p>
                        <div className="flex items-center gap-4 mt-2" data-id="element-1400">
                          <div className="flex items-center border rounded-lg" data-id="element-1401">
                            <button onClick={() => updateQuantity(item.id, -1)} className="p-1 hover:bg-gray-50" data-id="element-1402">
                              <Minus className="w-4 h-4" data-id="element-1403" />
                            </button>
                            <span className="w-8 text-center text-sm font-bold" data-id="element-1404">
                              {item.quantity}
                            </span>
                            <button onClick={() => updateQuantity(item.id, 1)} className="p-1 hover:bg-gray-50" data-id="element-1405">
                              <Plus className="w-4 h-4" data-id="element-1406" />
                            </button>
                          </div>
                          <button onClick={() => removeFromCart(item.id)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-1407">
                            <Trash2 className="w-4 h-4" data-id="element-1408" />
                          </button>
                        </div>
                      </div>
                    </div>)}
              </div>

              {cart.length > 0 && <div className="p-6 border-t bg-gray-50 space-y-4" data-id="element-1409">
                  <div className="flex justify-between text-lg font-bold text-[#0A1F44]" data-id="element-1410">
                    <span data-id="element-1411">Total</span>
                    <span data-id="element-1412">${cartTotal.toFixed(2)}</span>
                  </div>
                  <Button onClick={handleCheckout} variant="primary" className="w-full py-4 text-lg font-bold rounded-xl shadow-lg shadow-red-600/20" data-id="element-1413">
                    Checkout Now <ArrowRight className="w-5 h-5 ml-2" data-id="element-1414" />
                  </Button>
                </div>}
            </motion.div>
          </>}
      </AnimatePresence>
    </Layout>;
}